<template>
  <div
    v-if="visible"
    ref="floatRef"
    class="pm-bubble"
    :style="floatStyle"
    role="toolbar"
    aria-label="Text formatting"
  >
    <button
      type="button"
      class="pm-bubble__btn"
      :class="{ 'is-active': isMark('strong') }"
      aria-label="Bold"
      data-testid="bubble-bold"
      @mousedown.prevent
      @click="run(toggleMark(schema.marks.strong))"
    >
      <b>B</b>
    </button>
    <button
      type="button"
      class="pm-bubble__btn"
      :class="{ 'is-active': isMark('em') }"
      aria-label="Italic"
      data-testid="bubble-italic"
      @mousedown.prevent
      @click="run(toggleMark(schema.marks.em))"
    >
      <i>I</i>
    </button>
    <button
      type="button"
      class="pm-bubble__btn"
      :class="{ 'is-active': isMark('underline') }"
      aria-label="Underline"
      data-testid="bubble-underline"
      @mousedown.prevent
      @click="run(toggleMark(schema.marks.underline))"
    >
      <u>U</u>
    </button>
    <button
      type="button"
      class="pm-bubble__btn"
      :class="{ 'is-active': isMark('strike') }"
      aria-label="Strikethrough"
      data-testid="bubble-strike"
      @mousedown.prevent
      @click="run(toggleMark(schema.marks.strike))"
    >
      <s>S</s>
    </button>
    <button
      type="button"
      class="pm-bubble__btn"
      :class="{ 'is-active': isMark('code') }"
      aria-label="Inline code"
      data-testid="bubble-code"
      @mousedown.prevent
      @click="run(toggleMark(schema.marks.code))"
    >
      <span class="pm-bubble__mono">{}</span>
    </button>
    <span class="pm-bubble__divider" />
    <button
      type="button"
      class="pm-bubble__btn"
      :class="{ 'is-active': isMark('link') }"
      aria-label="Link"
      data-testid="bubble-link"
      @mousedown.prevent
      @click="onLinkClick"
    >
      <span class="pm-bubble__small">link</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import {
  ref, computed, watch, shallowRef,
} from 'vue';
import { useFloating, autoUpdate, flip, shift, offset } from '@floating-ui/vue';
import { toggleMark as pmToggleMark } from 'prosemirror-commands';
import type { Command } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import { pmSchema } from './pmSchema';

const props = defineProps<{
  view: EditorView | null
  rect: DOMRect | null
  visible: boolean
}>();

const schema = pmSchema;
const floatRef = ref<HTMLElement | null>(null);

// Virtual reference for floating-ui from the selection rect.
const virtualRef = computed(() => {
  const r = props.rect;
  if (!r) return null;
  return {
    getBoundingClientRect: () => r,
  };
});

const reference = shallowRef<{ getBoundingClientRect: () => DOMRect } | null>(null);
watch(virtualRef, (v) => { reference.value = v; }, { immediate: true });

const { floatingStyles } = useFloating(reference, floatRef, {
  placement: 'top',
  middleware: [offset(8), flip(), shift({ padding: 8 })],
  whileElementsMounted: autoUpdate,
});

const floatStyle = computed(() => floatingStyles.value);

const toggleMark = pmToggleMark;

function isMark(name: string): boolean {
  const view = props.view;
  if (!view) return false;
  const markType = schema.marks[name];
  if (!markType) return false;
  const { from, $from, to, empty } = view.state.selection;
  if (empty) return !!markType.isInSet(view.state.storedMarks || $from.marks());
  return view.state.doc.rangeHasMark(from, to, markType);
}

function run(cmd: Command) {
  const view = props.view;
  if (!view) return;
  cmd(view.state, view.dispatch, view);
  view.focus();
}

function onLinkClick() {
  const view = props.view;
  if (!view) return;
  const linkType = schema.marks.link;
  if (!linkType) return;
  const { from, to, empty } = view.state.selection;
  if (empty) return;
  if (view.state.doc.rangeHasMark(from, to, linkType)) {
    view.dispatch(view.state.tr.removeMark(from, to, linkType));
    view.focus();
    return;
  }
  const href = window.prompt('Enter URL', 'https://');
  if (!href) {
    view.focus();
    return;
  }
  view.dispatch(view.state.tr.addMark(from, to, linkType.create({ href, target: '_blank' })));
  view.focus();
}
</script>

<style scoped>
.pm-bubble {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 50;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  background: #111827;
  color: #f9fafb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  font-size: 13px;
}

.pm-bubble__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  background: transparent;
  border: 0;
  color: #f9fafb;
  cursor: pointer;
  border-radius: 5px;
}

.pm-bubble__btn:hover {
  background: rgba(255, 255, 255, 0.12);
}

.pm-bubble__btn.is-active {
  background: rgba(255, 255, 255, 0.2);
}

.pm-bubble__divider {
  width: 1px;
  height: 18px;
  background: rgba(255, 255, 255, 0.18);
  margin: 0 4px;
}

.pm-bubble__mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
}

.pm-bubble__small {
  font-size: 12px;
}
</style>
